import { useMemo } from "react";

// Palette for initial-based avatars (deterministic per identifier)
const COLORS = [
  "bg-rose-500",
  "bg-amber-500",
  "bg-emerald-500",
  "bg-teal-500",
  "bg-sky-500",
  "bg-indigo-500",
  "bg-violet-500",
  "bg-fuchsia-500",
  "bg-pink-500",
  "bg-orange-500",
];

function hashString(str) {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = (h * 31 + str.charCodeAt(i)) & 0xffffffff;
  }
  return Math.abs(h);
}

function getInitials(name, email) {
  const source = (name || email || "?").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  // single token — use first char of that, or first two letters of email local-part
  const local = (email || source).split("@")[0];
  return (local.slice(0, 2) || "?").toUpperCase();
}

/**
 * Universal avatar.
 * Props:
 *  - src: optional avatar URL (data: URL or http)
 *  - name: display name for initials
 *  - email: email fallback for initials & color seed
 *  - size: tailwind size preset: "xs" | "sm" | "md" | "lg" | "xl"
 *  - className: extra classes for the outer element
 *  - online: if true, shows a green presence dot overlay at bottom-right
 */
export default function Avatar({ src, name, email, size = "md", className = "", online = false }) {
  const sizeClass = {
    xs: "w-6 h-6 text-[10px]",
    sm: "w-8 h-8 text-xs",
    md: "w-10 h-10 text-sm",
    lg: "w-14 h-14 text-lg",
    xl: "w-20 h-20 text-2xl",
  }[size] || "w-10 h-10 text-sm";

  const dotSize = {
    xs: "w-1.5 h-1.5",
    sm: "w-2 h-2",
    md: "w-2.5 h-2.5",
    lg: "w-3 h-3",
    xl: "w-4 h-4",
  }[size] || "w-2.5 h-2.5";

  const initials = useMemo(() => getInitials(name, email), [name, email]);
  const colorClass = useMemo(() => {
    const seed = (email || name || "anon").toLowerCase();
    return COLORS[hashString(seed) % COLORS.length];
  }, [email, name]);

  const avatarEl = src ? (
    <img
      src={src}
      alt={name || email || "avatar"}
      className={`${sizeClass} rounded-full object-cover ring-2 ring-white/30`}
      data-testid="user-avatar-image"
    />
  ) : (
    <div
      className={`${sizeClass} ${colorClass} rounded-full flex items-center justify-center font-bold text-white ring-2 ring-white/30`}
      data-testid="user-avatar-initials"
    >
      {initials}
    </div>
  );

  if (!online) return <div className={`relative inline-block ${className}`}>{avatarEl}</div>;

  return (
    <div className={`relative inline-block ${className}`} data-testid="user-avatar-online">
      {avatarEl}
      <span
        className={`absolute bottom-0 right-0 ${dotSize} rounded-full bg-green-500 ring-2 ring-white`}
        title="Online now"
        aria-label="Online now"
      />
    </div>
  );
}
